'use client'

import { useIntl } from 'react-intl'
import { Bubble, Button, Cell, Divider, Image, Spacer, Text } from '@vinted/web-ui'
import classNames from 'classnames'
import { useContext, useEffect, useState } from 'react'

import { UserPhoto } from '@marketplace-web/domain/conversation-user'
import {
  ConversationListContext,
  useConversationContext,
} from '@marketplace-web/domain/core-conversation'
import {
  OfferRequestStatus,
  acceptOffer,
  rejectOffer,
  updateItemPrice,
  ITEM_PRICE_UPDATED_OFFER_ID_KEY,
  ConversationMessage,
  OfferRequestMessage as OfferRequestMessageType,
  ApplicableTransactionAction,
  TransactionStatus,
} from '@marketplace-web/domain/conversation-messaging-data'
import { useAbTest, useTrackAbTest, useTrackAbTestCallback } from '@marketplace-web/shared/ab-tests'
import { useBreakpoint } from '@marketplace-web/shared/breakpoints'
import {
  getLocalStorageItem,
  reloadPage,
  setLocalStorageItem,
} from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'
import { useAsset } from '@marketplace-web/domain/assets'
import { isDateInTheFuture } from '@marketplace-web/shared/utils'
import { ConversationActionButton } from '@marketplace-web/domain/conversation-action'

import {
  buyerBuyEvent,
  openOfferScreenEvent,
  sellerActionOfferRequest,
  updatePriceFromConversationEvent,
  UpdatePriceFromConversationEventAction,
  updatePriceFromConversationViewEvent,
} from '_libs/common/event-tracker/events'
import { formatCurrency } from '_libs/utils/formatString'
import { OFFER_REQUEST_ACCEPT_URL, OFFER_REQUEST_REJECT_URL } from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import { useNavigateToCheckout } from 'pages/SingleCheckout/hooks'

import Action from './Action'
import { OfferRequestAction } from './types'
import MessageTime from '../../MessageTime'
import useTransactionActions from '../../../hooks/useTransactionActions/useTransactionActions'
import { BuyButtonPosition } from '../../../constants'

type OwnProps = {
  isGrouped: boolean
  message: OfferRequestMessageType
  openOfferModal: () => void
}

type Props = OwnProps & ConversationMessage

const OfferRequestMessage = ({ isGrouped, message, createdTime, openOfferModal }: Props) => {
  const translate = useTranslate('conversation')
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const asset = useAsset('/assets/kyc')

  const { navigateToCheckout } = useNavigateToCheckout()

  const trackAbTest = useTrackAbTestCallback()

  const { locale } = useIntl()
  const currentUserId = useSession().user?.id
  const { conversation, transaction, getConversationRequest } = useConversationContext()
  const { updateConversation } = useContext(ConversationListContext)
  const oppositeUser = conversation?.oppositeUser
  const { applicableTransactionActions } = useTransactionActions()

  const [isPriceUpdateLoading, setIsPriceUpdateLoading] = useState(false)

  const updateItemPriceAfterAcceptedOfferAbTest = useAbTest(
    'update_item_price_after_accepted_offer_v2',
  )
  const buyNowButtonInConversation = useAbTest('dd_tx_web_buy_now_button_in_conversation')
  useTrackAbTest(buyNowButtonInConversation)

  const isPriceUpdateAllowed =
    updateItemPriceAfterAcceptedOfferAbTest?.variant === 'on' && conversation?.isItemPriceUpdatable

  const { statusTitle, status, offerRequestId } = message
  const isBundleOffer = conversation?.transaction?.isBundle
  const itemId = conversation?.itemId
  const isCurrentAuthor = message.userId === String(currentUserId)
  const arePendingOfferActionsVisible = status === OfferRequestStatus.Pending && !isCurrentAuthor
  const isOfferStatusVisible = status !== OfferRequestStatus.Pending || isCurrentAuthor
  const isCounterOfferActionVisible =
    status === OfferRequestStatus.Rejected &&
    !isCurrentAuthor &&
    message.isCurrentOffer &&
    applicableTransactionActions?.includes(ApplicableTransactionAction.Offer)

  const messageClass = classNames('conversation-message', {
    'conversation-message__current-author': isCurrentAuthor,
    'conversation-message__single-action': isCounterOfferActionVisible,
    'conversation-message__actions': arePendingOfferActionsVisible,
  })

  const handleActionClick =
    (apiCall: typeof acceptOffer | typeof rejectOffer, action: OfferRequestAction) => async () => {
      if (!conversation?.transactionId) return

      const { price, originalPrice } = message
      const { transactionId } = conversation

      track(
        sellerActionOfferRequest({
          action,
          transactionId,
          offerRequestId: message.offerRequestId,
          currentPrice: originalPrice,
          offeredPrice: price,
        }),
      )

      await apiCall({
        transactionId,
        offerRequestId: Number(message.offerRequestId),
      })

      if (
        conversation.isItemPriceUpdatable &&
        action === OfferRequestAction.Accept &&
        !isBundleOffer &&
        itemId
      )
        trackAbTest(updateItemPriceAfterAcceptedOfferAbTest)

      getConversationRequest({
        conversationId: conversation.id,
      })

      updateConversation({ id: conversation.id })
    }

  const handleOpenOfferModal = () => {
    openOfferModal()

    if (!transaction) return

    track(
      openOfferScreenEvent({
        isBuyer: false,
        currentPrice: Number(transaction.offer.price.amount),
        currencyCode: transaction.offer.price.currencyCode,
        openedFrom: ClickableElement.MessageMakeOffer,
        transactionId: transaction.id,
      }),
    )
  }

  const handleChangePriceClick = async () => {
    if (!itemId) return
    const newPrice = Number(message.price)

    track(
      updatePriceFromConversationEvent({
        listingId: `${itemId}`,
        transactionId: `${conversation.transactionId}`,
        currentPrice: Number(message.originalPrice),
        newPrice,
        offerRequestId,
        action: UpdatePriceFromConversationEventAction.OnClick,
      }),
    )
    setIsPriceUpdateLoading(true)
    await updateItemPrice({ itemId, itemPrice: newPrice })
    setIsPriceUpdateLoading(false)

    const updatedPriceOfferIds = JSON.parse(
      getLocalStorageItem(ITEM_PRICE_UPDATED_OFFER_ID_KEY) || '[]',
    )
    updatedPriceOfferIds.push(offerRequestId)

    setLocalStorageItem(ITEM_PRICE_UPDATED_OFFER_ID_KEY, JSON.stringify(updatedPriceOfferIds))
    reloadPage()
  }

  const renderPrices = () => {
    const { price, originalPrice, currency } = message

    const priceLabel = formatCurrency(price, { currency, locale })
    const originalPriceLabel = formatCurrency(originalPrice, { currency, locale })

    return (
      <div>
        <Text as="h2" text={priceLabel} type={Text.Type.Title} testId="offer-current-price-label" />
        <Spacer orientation={Spacer.Orientation.Vertical} size={Spacer.Size.Small} />
        <Text
          as="h2"
          text={originalPriceLabel}
          type={Text.Type.Title}
          theme="muted"
          strikethrough
          testId="offer-original-price-label"
        />
      </div>
    )
  }

  const renderActionState = () => {
    if (isCounterOfferActionVisible) {
      return (
        <>
          <Spacer />
          <Button
            text={translate('offer_request.offer_your_price')}
            size={Button.Size.Medium}
            styling={Button.Styling.Filled}
            onClick={handleOpenOfferModal}
            testId="counter-offer-button"
          />
        </>
      )
    }

    if (!arePendingOfferActionsVisible || !conversation?.transactionId) return null

    return (
      <>
        <Spacer />
        <Action
          text={translate('offer_request.accept')}
          url={OFFER_REQUEST_ACCEPT_URL(conversation.transactionId, message.offerRequestId)}
          onClick={handleActionClick(acceptOffer, OfferRequestAction.Accept)}
          styling={Action.Styling.Filled}
          testId="offer-message-accept-button"
        />
        <Spacer />
        <div className="u-flexbox">
          <div className="u-half-width">
            <Action
              text={translate('offer_request.decline')}
              url={OFFER_REQUEST_REJECT_URL(conversation.transactionId, message.offerRequestId)}
              onClick={handleActionClick(rejectOffer, OfferRequestAction.Reject)}
              testId="offer-message-decline-button"
            />
          </div>
          <Spacer orientation={Spacer.Orientation.Vertical} />
          <div className="u-half-width">
            <Button
              text={translate('offer_request.offer_your_price')}
              size={Button.Size.Medium}
              onClick={handleOpenOfferModal}
              testId="counter-offer-button"
            />
          </div>
        </div>
      </>
    )
  }

  useEffect(() => {
    if (isCurrentAuthor || isBundleOffer || !isPriceUpdateAllowed) return

    if (
      status === OfferRequestStatus.Accepted &&
      transaction?.status === TransactionStatus.StatusNew
    )
      track(
        updatePriceFromConversationViewEvent({
          listingId: `${itemId}`,
          transactionId: `${conversation?.transactionId}`,
          currentPrice: Number(message.originalPrice),
          newPrice: Number(message.price),
          offerRequestId: message.offerRequestId,
          target: 'update_price_button',
        }),
      )
  }, [
    isCurrentAuthor,
    isBundleOffer,
    isPriceUpdateAllowed,
    status,
    itemId,
    message,
    conversation,
    track,
    transaction?.status,
  ])

  const handleBuyButtonClick = () => {
    if (!conversation?.transaction?.itemId) return
    if (!conversation?.transaction?.id) return

    track(
      buyerBuyEvent({
        transactionId: conversation.transaction.id,
        screen: Screen.MessageReply,
        buttonPosition: BuyButtonPosition.ConversationMessageOfferRequest,
      }),
    )

    if (!conversation?.transactionId) return

    navigateToCheckout(conversation.transactionId)
  }

  const renderUserPhoto = () => {
    if (!oppositeUser) return null

    const { photo, profileUrl, isSystem } = oppositeUser

    return (
      <div className="u-ui-margin-right-regular u-inline-block u-float-left">
        <UserPhoto
          photo={photo}
          profileUrl={profileUrl}
          isSystem={isSystem}
          isCurrentAuthor={isCurrentAuthor}
        />
      </div>
    )
  }

  const renderCurrencyExchangeRateNote = () => {
    const { isPriceFixed } = message

    if (isPriceFixed) return null
    if (status !== OfferRequestStatus.Pending && status !== OfferRequestStatus.Accepted) return null

    return (
      <>
        <Text as="h4" type={Text.Type.Caption} text={translate('currency_exchange_rate_note')} />
        <Spacer />
      </>
    )
  }

  const renderStatus = () => {
    if (!isOfferStatusVisible) return null

    return (
      <Text as="span" type={Text.Type.UserInput} text={statusTitle} testId="offer-status-title" />
    )
  }

  const getWasOfferPriceUpdated = () => {
    const offerRequestIdsParsed = JSON.parse(
      getLocalStorageItem(ITEM_PRICE_UPDATED_OFFER_ID_KEY)?.toString() || '[]',
    )
    const offerRequestIds = Array.isArray(offerRequestIdsParsed)
      ? offerRequestIdsParsed.map(id => id.toString())
      : []

    return offerRequestIds.includes(offerRequestId)
  }

  const renderItemPriceUpdatedStatus = () => {
    if (!isPriceUpdateAllowed || isCurrentAuthor || isBundleOffer) return null

    const offerPriceLabel = formatCurrency(message.price, { currency: message.currency, locale })
    const wasOfferPriceUpdated = getWasOfferPriceUpdated()

    if (wasOfferPriceUpdated)
      return (
        <div className="u-ui-padding-top-regular">
          <div className="conversation-message__status">
            <Divider />
            <Cell
              title={translate('offer_request.updated_price_title', { amount: offerPriceLabel })}
              body={
                <Text
                  as="div"
                  type={Text.Type.Subtitle}
                  text={translate('offer_request.updated_price_info')}
                />
              }
            />
            <Divider />
          </div>
        </div>
      )

    return null
  }

  const renderItemPriceUpdateAction = () => {
    const wasOfferPriceUpdated = getWasOfferPriceUpdated()

    if (!isPriceUpdateAllowed || isCurrentAuthor || isBundleOffer || wasOfferPriceUpdated)
      return null

    const offerPriceLabel = formatCurrency(message.price, { currency: message.currency, locale })
    const shouldSuggestUpdateItemPrice =
      status === OfferRequestStatus.Accepted && transaction?.status === TransactionStatus.StatusNew

    if (shouldSuggestUpdateItemPrice) {
      return (
        <div className="u-ui-padding-top-regular">
          <div className="conversation-message__status">
            <Divider />
            <Cell data-testid="update-price-suggestion">
              <Cell
                styling={Cell.Styling.Tight}
                title={translate('offer_request.update_price_suggestion_title')}
                prefix={<Image src={asset('cash.svg')} />}
                body={
                  <>
                    <Text
                      as="span"
                      type={Text.Type.Subtitle}
                      html
                      text={translate('offer_request.update_price_suggestion_description')}
                      format
                    />
                    <>
                      <Spacer size={Spacer.Size.Large} />
                      <div className={breakpoints.phones ? '' : 'u-flexbox'}>
                        <div>
                          <ConversationActionButton
                            isLoading={isPriceUpdateLoading}
                            text={translate('offer_request.change_price_to', {
                              amount: offerPriceLabel,
                            })}
                            size={Button.Size.Medium}
                            onClick={handleChangePriceClick}
                          />
                        </div>
                      </div>
                    </>
                  </>
                }
              />
            </Cell>
            <Divider />
          </div>
        </div>
      )
    }

    return null
  }

  const isBuyButtonShown = () => {
    const showPaymentsButtons = conversation?.isPaymentActionAvailable
    const isBuyer = conversation?.buyerId === currentUserId
    const isOfferAccepted = status === OfferRequestStatus.Accepted

    const isSellerCountdownActive = isDateInTheFuture(transaction?.seller?.closetCountdownEndDate)

    return (
      message.isCurrentOffer &&
      showPaymentsButtons &&
      isBuyer &&
      isOfferAccepted &&
      !isSellerCountdownActive
    )
  }

  const renderBuyButton = () => {
    if (!buyNowButtonInConversation || buyNowButtonInConversation?.variant === 'off') return null
    if (!conversation?.transactionId || isBuyButtonShown() === false) return null

    return (
      <>
        <Spacer />
        <Button
          text={translate('offer_message.buy')}
          size={Button.Size.Medium}
          styling={Button.Styling.Filled}
          onClick={handleBuyButtonClick}
          testId="offer-message-buy-button"
        />
      </>
    )
  }

  return (
    <>
      {!isGrouped && <MessageTime time={createdTime} />}
      <div className="conversation-message-container u-ui-margin-top-medium u-fill-width u-inline-block">
        {renderUserPhoto()}
        <div className={messageClass}>
          <Bubble inverse={isCurrentAuthor}>
            {renderCurrencyExchangeRateNote()}
            {renderPrices()}
            {renderStatus()}
            {renderActionState()}
            {renderBuyButton()}
          </Bubble>
        </div>
      </div>
      {renderItemPriceUpdateAction()}
      {renderItemPriceUpdatedStatus()}
    </>
  )
}

export default OfferRequestMessage
